import { useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Loader2, Paperclip, X } from "lucide-react";
import { toast } from "sonner";
import { PageHero } from "@/components/shared/PageHero";
import { Reveal } from "@/components/shared/Reveal";
import { PRODUCTS } from "@/data/products";
import { SERVICES } from "@/data/services";
import { INDUSTRIES } from "@/data/industries";
import { useSEO } from "@/lib/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACCEPT = ".pdf,.dwg,.dxf,.step,.stp,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx,.zip";

const inputCls =
  "w-full bg-ink border border-line px-4 py-3 text-sm text-steel-light placeholder:text-steel/40 focus:border-amber-brand focus:outline-none";

const Field = ({ label, error, children, required, hint }) => (
  <div>
    <label className="block font-mono-tech text-[11px] uppercase tracking-[0.2em] text-steel mb-2">
      {label} {required ? <span className="text-amber-brand">*</span> : <span className="text-steel/40 normal-case tracking-normal">— optional</span>}
    </label>
    {children}
    {hint && !error && <p className="mt-1.5 text-xs text-steel/50">{hint}</p>}
    {error && <p className="mt-1.5 text-xs text-red-400" role="alert">{error}</p>}
  </div>
);

const initial = {
  contact_person: "", company_name: "", email: "", phone: "",
  city: "", state: "", country: "India",
  product_service: "", industry: "", requirement_type: "",
  roll_dimensions: "", core_material: "", covering_material: "", hardness: "",
  operating_conditions: "", quantity: "", message: "", website: "",
};

export default function RequestQuote() {
  useSEO({
    title: "Request a Quote",
    description:
      "Send Vikas Rolls your roll requirement — product or service, dimensions, materials and operating conditions. Technical fields are optional; submit what you know and we'll do the rest.",
  });

  const [form, setForm] = useState(initial);
  const [file, setFile] = useState(null);
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);
  const navigate = useNavigate();

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      setErrors((er) => ({ ...er, drawing: "File exceeds the 10 MB limit." }));
      e.target.value = "";
      return;
    }
    setErrors((er) => ({ ...er, drawing: undefined }));
    setFile(f);
  };

  const validate = () => {
    const errs = {};
    if (form.contact_person.trim().length < 2) errs.contact_person = "Please enter a contact person.";
    if (form.company_name.trim().length < 2) errs.company_name = "Please enter your company name.";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) errs.email = "Please enter a valid work email.";
    if (!/^[+\d][\d\s\-()]{6,24}$/.test(form.phone.trim())) errs.phone = "Please enter a valid phone number.";
    if (!form.product_service) errs.product_service = "Please select a product or service.";
    if (!consent) errs.consent = "Please accept the consent statement to proceed.";
    return errs;
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) {
      toast.error("Please review the highlighted fields.");
      return;
    }
    setSending(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("consent", consent ? "true" : "false");
      if (file) fd.append("drawing", file);
      await axios.post(`${API}/quotes`, fd);
      navigate("/thank-you");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again or email sales@vikasrolls.in.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <PageHero
        chapter="Q"
        label="Enquiry"
        title="Request a Quote"
        sub="Tell us what your line needs. Technical fields are optional — you can submit an enquiry even if all specifications are not yet available, and our team will work through the details with you."
        crumbs={[{ label: "Request a Quote" }]}
      />

      <section className="bg-ink py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-5 md:px-10">
          <Reveal>
            <form onSubmit={submit} noValidate className="grid gap-10" data-testid="quote-form">
              <input type="text" value={form.website} onChange={set("website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />

              <fieldset className="border border-line bg-ink-2 p-7 md:p-9 grid gap-6">
                <legend className="font-mono-tech text-[11px] uppercase tracking-[0.25em] text-amber-brand px-3">01 — Contact Details</legend>
                <div className="grid sm:grid-cols-2 gap-6">
                  <Field label="Contact Person" required error={errors.contact_person}>
                    <input className={inputCls} value={form.contact_person} onChange={set("contact_person")} placeholder="Full name" data-testid="quote-contact-person-input" />
                  </Field>
                  <Field label="Company Name" required error={errors.company_name}>
                    <input className={inputCls} value={form.company_name} onChange={set("company_name")} placeholder="Company" data-testid="quote-company-input" />
                  </Field>
                </div>
                <div className="grid sm:grid-cols-2 gap-6">
                  <Field label="Work Email" required error={errors.email}>
                    <input type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@company.com" data-testid="quote-email-input" />
                  </Field>
                  <Field label="Phone Number" required error={errors.phone}>
                    <input type="tel" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+91 ..." data-testid="quote-phone-input" />
                  </Field>
                </div>
                <div className="grid sm:grid-cols-3 gap-6">
                  <Field label="City"><input className={inputCls} value={form.city} onChange={set("city")} data-testid="quote-city-input" /></Field>
                  <Field label="State"><input className={inputCls} value={form.state} onChange={set("state")} data-testid="quote-state-input" /></Field>
                  <Field label="Country"><input className={inputCls} value={form.country} onChange={set("country")} data-testid="quote-country-input" /></Field>
                </div>
              </fieldset>

              <fieldset className="border border-line bg-ink-2 p-7 md:p-9 grid gap-6">
                <legend className="font-mono-tech text-[11px] uppercase tracking-[0.25em] text-amber-brand px-3">02 — Requirement</legend>
                <div className="grid sm:grid-cols-2 gap-6">
                  <Field label="Product or Service" required error={errors.product_service}>
                    <select className={inputCls} value={form.product_service} onChange={set("product_service")} data-testid="quote-product-select">
                      <option value="">Select…</option>
                      <optgroup label="Products">
                        {PRODUCTS.map((p) => <option key={p.slug} value={p.name}>{p.name}</option>)}
                      </optgroup>
                      <optgroup label="Technical Services">
                        {SERVICES.map((s) => <option key={s.slug} value={s.name}>{s.name}</option>)}
                      </optgroup>
                      <option value="Other / Not sure">Other / Not sure</option>
                    </select>
                  </Field>
                  <Field label="Industry">
                    <select className={inputCls} value={form.industry} onChange={set("industry")} data-testid="quote-industry-select">
                      <option value="">Select…</option>
                      {INDUSTRIES.map((i) => <option key={i.slug} value={i.name}>{i.name}</option>)}
                      <option value="Other">Other</option>
                    </select>
                  </Field>
                </div>
                <Field label="New Roll or Refurbishment">
                  <div className="flex flex-wrap gap-3" role="radiogroup" aria-label="New roll or refurbishment">
                    {["New Roll", "Refurbishment", "Not sure yet"].map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, requirement_type: opt }))}
                        data-testid={`quote-type-${opt.toLowerCase().replace(/\s/g, "-")}`}
                        aria-pressed={form.requirement_type === opt}
                        className={`border px-5 py-2.5 text-sm ${form.requirement_type === opt ? "border-amber-brand text-amber-brand bg-amber-brand/10" : "border-line text-steel hover:border-steel"}`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </Field>
                <div className="grid sm:grid-cols-2 gap-6">
                  <Field label="Roll Dimensions" hint="e.g. face length × diameter × journal — approximate is fine">
                    <input className={inputCls} value={form.roll_dimensions} onChange={set("roll_dimensions")} placeholder="e.g. 2400 × 450 mm" data-testid="quote-dimensions-input" />
                  </Field>
                  <Field label="Quantity">
                    <input className={inputCls} value={form.quantity} onChange={set("quantity")} placeholder="e.g. 2 rolls" data-testid="quote-quantity-input" />
                  </Field>
                </div>
                <div className="grid sm:grid-cols-3 gap-6">
                  <Field label="Core Material">
                    <input className={inputCls} value={form.core_material} onChange={set("core_material")} placeholder="e.g. EN8 steel" data-testid="quote-core-input" />
                  </Field>
                  <Field label="Covering / Elastomer">
                    <input className={inputCls} value={form.covering_material} onChange={set("covering_material")} placeholder="If known" data-testid="quote-covering-input" />
                  </Field>
                  <Field label="Required Hardness">
                    <input className={inputCls} value={form.hardness} onChange={set("hardness")} placeholder="If known" data-testid="quote-hardness-input" />
                  </Field>
                </div>
                <Field label="Operating Conditions" hint="Speed, load, temperature, chemicals, process medium — whatever you know">
                  <textarea rows={3} className={inputCls} value={form.operating_conditions} onChange={set("operating_conditions")} data-testid="quote-conditions-input" />
                </Field>
                <Field label="Message">
                  <textarea rows={4} className={inputCls} value={form.message} onChange={set("message")} placeholder="Anything else we should know…" data-testid="quote-message-input" />
                </Field>
                <Field label="Drawing / Specification Upload" error={errors.drawing} hint="PDF, DWG, DXF, STEP, image or office formats — max 10 MB">
                  {file ? (
                    <div className="flex items-center justify-between border border-line bg-ink px-4 py-3">
                      <span className="flex items-center gap-3 text-sm text-steel-light truncate">
                        <Paperclip size={15} className="text-amber-brand flex-none" /> {file.name}
                      </span>
                      <button type="button" onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ""; }} aria-label="Remove file" data-testid="quote-file-remove" className="text-steel hover:text-red-400 p-1">
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <label className="flex items-center gap-3 border border-dashed border-line px-4 py-4 text-sm text-steel cursor-pointer hover:border-amber-brand hover:text-amber-brand">
                      <Paperclip size={16} aria-hidden="true" /> Attach a drawing or specification
                      <input ref={fileRef} type="file" accept={ACCEPT} className="sr-only" onChange={onFile} data-testid="quote-file-input" />
                    </label>
                  )}
                </Field>
              </fieldset>

              <div className="grid gap-5">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    data-testid="quote-consent-checkbox"
                    className="mt-1 w-4 h-4 accent-[#f59e0b]"
                  />
                  <span className="text-sm text-steel leading-relaxed">
                    I consent to Vikas Rolls Private Limited storing and using the details above to
                    respond to this enquiry. <span className="text-amber-brand">*</span>
                  </span>
                </label>
                {errors.consent && <p className="text-xs text-red-400" role="alert" data-testid="quote-consent-error">{errors.consent}</p>}

                <button
                  type="submit"
                  disabled={sending}
                  data-testid="quote-submit-button"
                  className="group inline-flex items-center justify-center gap-3 bg-amber-brand text-ink font-display font-bold px-8 py-4 text-lg hover:bg-steel-light disabled:opacity-60"
                >
                  {sending ? <Loader2 size={19} className="animate-spin" /> : null}
                  {sending ? "Submitting…" : "Submit Enquiry"}
                  {!sending && <ArrowRight size={19} className="transition-transform duration-300 group-hover:translate-x-1.5" />}
                </button>
                <p className="text-xs text-steel/60">
                  Prefer email? Write to <Link to="/contact" className="text-amber-brand underline underline-offset-4">sales@vikasrolls.in</Link> with your drawings attached.
                </p>
              </div>
            </form>
          </Reveal>
        </div>
      </section>
    </>
  );
}
